// Vendor imports
const _ = require('lodash')
const React = require('react')
const PropTypes = require('prop-types')
const classNames = require('classnames')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const { ModalDepthModifier } = require('client-v2/common/contexts/modal-depth')
// Sibling imports
const LightweightEngagementTutorialCard = require('./card')
// Swipe constants
const MAX_SWIPE_WIDTH_FACTOR = 0.9
const MIN_SWIPE_WIDTH_FACTOR = 0.25
const BROWSER_BACK_SWIPE_RANGE = 40 // px

const DEBOUNCE_DELAY = 300 // ms
const DEBOUNCE_OPTIONS = { leading: true, trailing: false }

// BEM constants
const bem = BemEntity('LightweightEngagementTutorialModal')

class LightweightEngagementTutorialModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      currentIndex: 0,
      // Swipe state
      isSwiping: false,
      swipeWidth: 0,
      swipe: null
    }

    this.currentSlideRef = React.createRef()

    // Bind and debounce event listeners
    this.onSwipeLeft = _.debounce(this.onSwipeLeft.bind(this), DEBOUNCE_DELAY, DEBOUNCE_OPTIONS)
    this.onSwipeRight = _.debounce(this.onSwipeRight.bind(this), DEBOUNCE_DELAY, DEBOUNCE_OPTIONS)
    this.onSwipeStart = this.onSwipeStart.bind(this)
    this.onSwipeMove = this.onSwipeMove.bind(this)
    this.onSwipeEnd = this.onSwipeEnd.bind(this)
  }

  onSwipeLeft() {
    if (this.state.currentIndex === 0) {
      return
    }
    this.setState(({ currentIndex }) => ({
      currentIndex: currentIndex - 1
    }))
  }

  onSwipeRight() {
    if (this.state.currentIndex + 1 >= this.props.slideCount) {
      this.props.onComplete()
      return
    }
    this.setState(({ currentIndex }) => ({
      currentIndex: currentIndex + 1
    }))
  }

  onSwipeStart(event) {
    const currentSlideEl = this.currentSlideRef.current
    if (!this.props.isVisible || !currentSlideEl || this.state.isSwiping) {
      return
    }
    const { width } = currentSlideEl.getBoundingClientRect()
    this.setState(() => ({
      isSwiping: true,
      swipeWidth: 0,
      swipe: {
        touchStartX: _.get(event, 'touches[0].pageX'),
        touchStartY: _.get(event, 'touches[0].pageY'),
        maxWidth: width * MAX_SWIPE_WIDTH_FACTOR
      }
    }))
  }

  onSwipeMove(event) {
    const { isSwiping, swipe } = this.state
    if (!isSwiping) {
      return
    }
    // Determine the delta of the movement
    const moveX = _.get(event, 'touches[0].pageX')
    const moveY = _.get(event, 'touches[0].pageY')
    const diffX = swipe.touchStartX - moveX
    const diffY = swipe.touchStartY - moveY
    // Cancel the swipe if there's a more-important gesture
    const isVerticalSwipe = Math.abs(diffY) > Math.abs(diffX)
    const isBrowserBackSwipe = swipe.touchStartX < BROWSER_BACK_SWIPE_RANGE
    if (isVerticalSwipe || isBrowserBackSwipe) {
      this.setState(() => ({ swipeWidth: 0 }))
      return
    }
    // Determine which slide to swipe in and how far
    let swipeWidth = 0
    if (diffX < 0) {
      swipeWidth = Math.min(-diffX, swipe.maxWidth)
    } else if (diffX > 0) {
      swipeWidth = Math.max(-diffX, -swipe.maxWidth)
    }
    // Peek the next/previous slide in by the swipe amount
    this.setState(() => ({ swipeWidth }))
  }

  onSwipeEnd() {
    if (!this.state.isSwiping) {
      return
    }
    const { swipeWidth, swipe } = this.state
    const threshold = swipe.maxWidth * MIN_SWIPE_WIDTH_FACTOR
    // Clear swipe state
    this.setState(() => ({
      isSwiping: false,
      swipeWidth: 0,
      swipe: null
    }))
    // Determine if swipe triggered index update
    if (Math.abs(swipeWidth) > threshold) {
      if (swipeWidth > 0) {
        this.onSwipeLeft()
      }
      if (swipeWidth < 0) {
        this.onSwipeRight()
      }
    }
  }

  componentDidMount() {
    if (typeof window === 'object') {
      document.addEventListener('touchstart', this.onSwipeStart)
      document.addEventListener('touchmove', this.onSwipeMove)
      document.addEventListener('touchend', this.onSwipeEnd)
    }
  }

  componentWillUnmount() {
    if (typeof window === 'object') {
      document.removeEventListener('touchstart', this.onSwipeStart)
      document.removeEventListener('touchmove', this.onSwipeMove)
      document.removeEventListener('touchend', this.onSwipeEnd)
    }
  }

  render() {
    const { props, state, currentSlideRef, onSwipeLeft, onSwipeRight } = this
    const { currentIndex, isSwiping, swipeWidth } = state
    if (!props.isVisible) {
      return null
    }
    return (
      tag('div', {
        className: classNames(
          props.className,
          bem.block()
        )
      },
        tag(ModalDepthModifier),
        tag('div', {
          className: bem.element('backdrop')
        }),
        tag(LightweightEngagementTutorialModalContent, {
          ...props,
          currentIndex,
          currentSlideRef,
          isSwiping,
          swipeWidth,
          onSwipeLeft,
          onSwipeRight
        }),
        tag(LightweightEngagementTutorialModalSwipeButtons, {
          ...props,
          currentIndex,
          onSwipeLeft,
          onSwipeRight
        })
      )
    )
  }
}

LightweightEngagementTutorialModal.propTypes = {
  // Flags
  isVisible: PropTypes.bool.isRequired,
  // Values
  className: PropTypes.string,
  slides: PropTypes.arrayOf(PropTypes.shape({
    messageHtml: PropTypes.string,
    messageText: PropTypes.string,
    posterUrl: PropTypes.string,
    videoUrl: PropTypes.string
  })),
  slideCount: PropTypes.number,
  // Callbacks
  onComplete: PropTypes.func.isRequired
}

module.exports = LightweightEngagementTutorialModal

// BEM Block entity Element components

function LightweightEngagementTutorialModalSwipeButtons(props) {
  return (
    tag('div', {
      className: bem.element('buttons-container')
    },
      tag(LightweightEngagementTutorialModalSwipeLeftButton, props),
      tag(LightweightEngagementTutorialModalSwipeRightButton, props)
    )
  )
}

function LightweightEngagementTutorialModalSwipeLeftButton(props) {
  if (props.currentIndex <= 0) {
    return null
  }
  return (
    tag('button', {
      className: bem.element('swipe-button', 'left'),
      type: 'button',
      onClick: props.onSwipeLeft
    })
  )
}

function LightweightEngagementTutorialModalSwipeRightButton(props) {
  const { currentIndex } = props
  const lastSlideIndex = props.slideCount - 1
  return (
    tag('button', {
      className: bem.element('swipe-button', {
        right: currentIndex < lastSlideIndex,
        finish: currentIndex === lastSlideIndex
      }),
      type: 'button',
      onClick: props.onSwipeRight
    })
  )
}

function LightweightEngagementTutorialModalContent(props) {
  const { isSwiping, swipeWidth, ...slideProps } = props
  const renderSlide = slideIndexOffset => (
    tag(LightweightEngagementTutorialModalSlide, {
      ...slideProps,
      // There is no unique key; falling back to index since these don't re-order.
      key: `${props.currentIndex + slideIndexOffset}`,
      slideIndexOffset
    })
  )

  return (
    tag('div', {
      className: bem.element('content', {
        swiping: isSwiping
      }),
      style: { transform: `translateX(${swipeWidth}px)` }
    },
      [
        renderSlide(-1),
        renderSlide(0),
        renderSlide(+1)
      ]
    )
  )
}

function LightweightEngagementTutorialModalSlide(props) {
  const { currentIndex, slideCount, slideIndexOffset } = props
  const slideIndex = props.currentIndex + slideIndexOffset
  const slide = props.slides[slideIndex]
  if (!slide) {
    return null
  }
  return (
    tag('div', {
      ref: slideIndexOffset ? null : props.currentSlideRef,
      className: bem.element('slide-outer', {
        previous: slideIndexOffset < 0,
        current: slideIndexOffset === 0,
        next: slideIndexOffset > 0
      })
    },
      tag('div', {
        className: bem.element('slide-inner')
      },
        tag(LightweightEngagementTutorialCard, {
          ...slide,
          currentIndex,
          slideCount,
          slideIndex,
          onComplete: props.onComplete,
          onNext: props.onSwipeRight
        })
      )
    )
  )
}
